import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combine class names with conditional support and Tailwind conflict
 * resolution. Without twMerge, conflicting utilities (e.g. shadcn
 * CardContent's "p-6 pt-0" merged with a caller's "p-4") would all render
 * and CSS source order would decide the winner — usually wrong. twMerge
 * keeps only the last conflicting utility, matching caller intent.
 */
export const cn = (...inputs) => {
  return twMerge(clsx(inputs));
};

// IST = UTC+5:30. Return the current date/time as seen in Kolkata,
// regardless of what timezone the browser is set to.
const _istParts = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date()).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});

/** Today's date in IST as "YYYY-MM-DD". Safe to use as <input type="date"> default. */
export const todayIST = () => {
  const p = _istParts();
  return `${p.year}-${p.month}-${p.day}`;
};

/** Current ISO datetime string in IST, formatted as "YYYY-MM-DDTHH:MM:SS+05:30". */
export const nowIST = () => {
  const p = _istParts();
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}+05:30`;
};

/**
 * Parse a backend timestamp robustly as UTC.
 *
 * The backend stores every datetime as UTC (Python `datetime.utcnow()`
 * + tz-naive MySQL DATETIME columns) but Pydantic serializes them
 * WITHOUT a "Z" suffix — e.g. "2026-05-28T08:10:22". Per the ECMAScript
 * spec, an ISO string without a TZ designator is parsed as LOCAL time,
 * so a browser in IST would display 08:10 instead of 13:40.
 *
 * Convention enforced here: any string the backend hands us is UTC.
 * If it lacks a Z / explicit offset, append Z before parsing. Pure
 * date strings ("2026-05-28") are left alone (treated as a calendar
 * date, not a wall-clock instant).
 */
export const parseBackendDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value;
  let s = String(value).trim();
  if (!s) return null;
  // Date-only — no time component, treat as-is.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  if (!isDateOnly) {
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
    if (!hasTz) {
      // "2026-05-28 08:10:22" or "2026-05-28T08:10:22" → append Z.
      s = s.replace(' ', 'T') + 'Z';
    }
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The IST wall-clock "HH:mm" of a stored backend datetime. */
export const istClock = (value) => {
  const d = parseBackendDate(value);
  if (!d) return null;
  return d.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata', hourCycle: 'h23', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * True when a task datetime carries an explicit clock time.
 *
 * Task start/end are DateTime columns, but a task saved without a time lands on
 * IST-midnight. We treat 00:00 as "no time set" — that's what lets the UI show a
 * bare date for those and a date+time for the rest.
 */
export const hasExplicitTime = (value) => {
  const hhmm = istClock(value);
  return Boolean(hhmm) && hhmm !== '00:00';
};

/**
 * The instant a task is actually DUE.
 *
 * A task saved without a time is stored at IST-midnight. Taking that literally
 * makes every task due "today" overdue from 00:00:01 — the bug this fixes. So a
 * midnight end_date means "no time set" and is due at the END of that IST day;
 * an explicit time is honoured exactly. Use this for any status/overdue maths
 * instead of comparing the raw end_date.
 */
export const taskDueInstant = (value) => {
  const d = parseBackendDate(value);
  if (!d) return null;
  if (hasExplicitTime(value)) return d;
  const ymd = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  return new Date(`${ymd}T23:59:59.999+05:30`);
};

/**
 * Is a task overdue? Past its due instant and not done. Uses taskDueInstant so a
 * no-clock (IST-midnight) due date is treated as END of that day — otherwise a
 * task due today would read as overdue from 00:00. (Lives here, not in the Tasks
 * page, so the overdue list/count logic — ticket T26 — is unit-testable.)
 */
export const isOverdue = (t) => {
  if (!t || !t.end_date) return false;
  if (t.stage_type === 'done') return false;
  return taskDueInstant(t.end_date) < new Date();
};

/**
 * Format a date string to DD/MM/YYYY (calendar date in IST).
 *
 * Uses Asia/Kolkata explicitly so a server-emitted "2026-05-27T20:30:00Z"
 * (which is 2026-05-28 02:00 IST) renders as 28/05/2026 — the date the
 * user actually experienced, not the UTC calendar date.
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = parseBackendDate(dateString);
  if (!date) return '-';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(date).replaceAll('/', '/'); // en-GB returns DD/MM/YYYY
  } catch {
    return '-';
  }
};

/**
 * Format a date + time string in IST (DD MMM YY, HH:MM).
 * Use for audit logs and any field where the time of day matters.
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = parseBackendDate(dateString);
  if (!date) return '-';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(date);
  } catch {
    return '-';
  }
};

/**
 * Format a number as currency (INR)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0.00';
  
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `₹${parseFloat(amount).toFixed(2)}`;
  }
};

/**
 * Format a number with commas for thousands
 * @param {number} number - The number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined) return '0';
  
  try {
    return new Intl.NumberFormat('en-IN').format(number);
  } catch (error) {
    return number.toString();
  }
};

/**
 * Capitalize first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} Capitalized string
 */
export const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Convert snake_case to Title Case
 * @param {string} str - The string to convert
 * @returns {string} Title case string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => capitalizeFirstLetter(word))
    .join(' ');
};

/**
 * Get initials from a name
 * @param {string} name - The full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Check if a date is in the past
 * @param {string|Date} dateString - The date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (dateString) => {
  try {
    const date = parseBackendDate(dateString);
    return date < new Date();
  } catch (error) {
    return false;
  }
};

/**
 * Check if a date is today (in IST).
 */
export const isToday = (dateString) => {
  try {
    const todayStr = todayIST();
    const date = parseBackendDate(dateString);
    if (!date) return false;
    const dateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
    return dateStr === todayStr;
  } catch (error) {
    return false;
  }
};

/**
 * Get time ago string (e.g., "2 hours ago")
 * @param {string|Date} dateString - The date to compare
 * @returns {string} Time ago string
 */
export const getTimeAgo = (dateString) => {
  try {
    // Backend timestamps are UTC without a 'Z'. Raw `new Date()` would read
    // them as local (IST) time → every "x ago" was ~5.5h too old. Parse via
    // parseBackendDate so naive UTC strings get a Z appended first.
    const date = parseBackendDate(dateString);
    if (!date) return '-';
    const now = new Date();
    const secondsAgo = Math.floor((now - date) / 1000);

    if (secondsAgo < 60) return 'just now';
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m ago`;
    if (secondsAgo < 86400) return `${Math.floor(secondsAgo / 3600)}h ago`;
    if (secondsAgo < 604800) return `${Math.floor(secondsAgo / 86400)}d ago`;
    
    return formatDate(dateString);
  } catch (error) {
    return '-';
  }
};

/**
 * Truncate text to specified length
 * @param {string} text - The text to truncate
 * @param {number} length - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, length = 50) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

/**
 * Check if object is empty
 * @param {object} obj - The object to check
 * @returns {boolean} True if object is empty
 */
export const isEmpty = (obj) => {
  if (!obj) return true;
  if (typeof obj !== 'object') return false;
  return Object.keys(obj).length === 0;
};

/**
 * Resolve the backend base URL for building absolute attachment links.
 *
 * Priority:
 *  1. REACT_APP_BACKEND_URL env var — set this on the production server
 *     (e.g. https://report360.rechargestudio.com).
 *  2. Current page origin — safe fallback when the frontend and backend
 *     share a domain via a reverse proxy (the standard production setup).
 *
 * In production (page served from a real host) a localhost value in
 * REACT_APP_BACKEND_URL is ignored and the same-origin fallback is used, so no
 * localhost link can leak to users. Only when the page is itself on localhost
 * (local dev, separate frontend/backend ports) is the localhost backend used.
 */
export const getBackendBaseUrl = () => {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
    return envUrl.replace(/\/$/, '');
  }
  // Local dev only: the frontend (:3002) and backend (:8003) are different
  // origins with no /uploads proxy, so static attachment links must point at the
  // backend explicitly. Gated on the page itself being on localhost — a
  // production page never is, so this can't leak a localhost link to users.
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocal && envUrl) return envUrl.replace(/\/$/, '');
    return origin;
  }
  return '';
};

/**
 * Convert any attachment path/URL to a safe absolute href.
 *
 * - GCS / absolute URLs (`https://…`) are returned unchanged.
 * - Relative paths (`/uploads/…`) are prefixed with getBackendBaseUrl().
 *
 * Use this everywhere the app opens or displays an attachment so that
 * production never generates localhost:8003 links.
 */
export const resolveAttachmentUrl = (path) => {
  if (!path) return '#';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = getBackendBaseUrl();
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
};
